<script lang="ts" setup>
import type { VariantType } from '#components/elements/button';
import { BUTTON_ICON_CLASS_SIZE, BUTTON_ICON_ICON_SIZE } from '#components/elements/button';
import ButtonBase from '#components/elements/button/ButtonBase.vue';
import type { IconName } from '#components/icons/icon.model';
import type { SizeType } from '#components/utils/sizes.type';
import type { PropType } from 'vue';
import { computed } from 'vue';
import VueFeather from 'vue-feather';
import type { RouteLocationRaw } from 'vue-router';

const props = defineProps({
  background: {
    type: String as PropType<'transparent' | 'outline' | ''>,
    required: false,
    default: ''
  },
  disabled: { type: Boolean, default: false },
  filled: { type: Boolean, default: false },
  variant: { type: String as PropType<VariantType>, default: 'default' },
  icon: { type: String as PropType<IconName>, default: undefined },
  size: { type: String as PropType<SizeType>, default: 'medium' },
  to: {
    type: [Object, String] as PropType<RouteLocationRaw>,
    required: false,
    default: undefined
  },
  isExternalLink: { type: Boolean, required: false, default: false }
});

const iconSize = computed(() => BUTTON_ICON_ICON_SIZE[props.size]);
const iconClass = computed(
  () =>
    (props.background === 'transparent' || props.background === 'outline') && {
      'text-black': props.variant === 'tertiary',
      'text-success': props.variant === 'success',
      'text-error': props.variant === 'error',
      'text-warning': props.variant === 'warning',
      'text-primary': props.variant === 'primary'
    }
);
const slotClass = computed(() => `w-${BUTTON_ICON_CLASS_SIZE[props.size]} h-${BUTTON_ICON_CLASS_SIZE[props.size]}`);
</script>

<template>
  <ButtonBase v-bind="{ background, variant, disabled, size, isExternalLink, to }" class="items-center !p-2">
    <VueFeather
      v-if="icon"
      v-bind="{ type: icon, size: iconSize }"
      aria-hidden="true"
      :class="iconClass"
      :fill="filled ? 'currentColor' : undefined"
    />
    <div v-else :class="slotClass">
      <slot />
    </div>
  </ButtonBase>
</template>
